import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/locales";

interface PaginationProps {
  total: number;
  page: number; // 这里的page是从0开始的
  limit: number;
  pageSizes?: number[];
  onPagination: (page: number, limit: number) => void; // 回调返回的page是从0开始的
  prevNextType?: "text" | "icon" | "both";
}

export function PaginationComponent({
  total,
  page: initialPage, // 接收0开始的页码
  limit: initialLimit,
  pageSizes = [10, 20, 30, 50],
  onPagination,
  prevNextType = "text",
}: PaginationProps) {
  // 内部维护1开始的页码用于显示
  const [currentPage, setCurrentPage] = useState(initialPage + 1);
  const [pageSize, setPageSize] = useState(initialLimit);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const { t } = useTranslation();

  // 同步外部传入的页码和每页条数（外部是0开始，内部转成1开始）
  useEffect(() => {
    setCurrentPage(initialPage + 1);
  }, [initialPage]);

  useEffect(() => {
    setPageSize(initialLimit);
  }, [initialLimit]);

  // 处理页码变化（内部1开始转成0开始传给外部）
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      onPagination(newPage - 1, pageSize); // 转成0开始
    }
  };

  // 处理每页条数变化（页码重置为1显示，实际传0给外部）
  const handlePageSizeChange = (value: string) => {
    const newSize = Number(value);
    setPageSize(newSize);
    setCurrentPage(1);
    onPagination(0, newSize); // 重置为0开始的第一页
  };

  // 生成页码列表（显示用1开始）
  const getPageNumbers = () => {
    const pages = [];
    pages.push(1);

    if (totalPages > 5) {
      if (currentPage > 3) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }
    } else {
      for (let i = 2; i < totalPages; i++) {
        pages.push(i);
      }
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  if (total <= 0) return null;

  // 渲染上一页内容
  const renderPrevButton = () => {
    const content = () => {
      switch (prevNextType) {
        case "text":
          return "上一页";
        case "icon":
          return (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          );
        case "both":
          return (
            <div className="flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
              <span>上一页</span>
            </div>
          );
        default:
          return "上一页";
      }
    };

    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`h-9 px-2 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {content()}
      </Button>
    );
  };

  // 渲染下一页内容
  const renderNextButton = () => {
    const content = () => {
      switch (prevNextType) {
        case "text":
          return "下一页";
        case "icon":
          return (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 18 6-6-6-6" />
            </svg>
          );
        case "both":
          return (
            <div className="flex items-center gap-1">
              <span>下一页</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </div>
          );
        default:
          return "下一页";
      }
    };

    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`h-9 px-2 ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {content()}
      </Button>
    );
  };

  return (
    <div style={{ position: "absolute", bottom: " 0", right: "30px" }}>
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 py-4" style={{ display: "inline-block", float: "right" }}>
        <div className="flex">
          {/* 分页控件 */}
          <Pagination>
            <div className="text-sm text-muted-foreground" style={{ lineHeight: "36px" }}>
              {t("admin.totalRecords", { count: total })}
            </div>
            <PaginationContent className="flex items-center">
              {/* 上一页按钮 */}
              <PaginationItem>{renderPrevButton()}</PaginationItem>

              {/* 页码 */}
              {getPageNumbers().map((page, index) => (
                <PaginationItem key={index}>
                  {typeof page === "number" ? (
                    <PaginationLink onClick={() => handlePageChange(page)} isActive={currentPage === page} className="h-9 w-9">
                      {page}
                    </PaginationLink>
                  ) : (
                    <span className="h-9 w-9 flex items-center justify-center">...</span>
                  )}
                </PaginationItem>
              ))}

              {/* 下一页按钮 */}
              <PaginationItem>{renderNextButton()}</PaginationItem>
            </PaginationContent>
          </Pagination>

          {/* 显示条数选择器 */}
          <div className="flex items-center gap-2" style={{ display: "inline-block" }}>
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-26">
                <SelectValue placeholder="条数" />
              </SelectTrigger>
              <SelectContent>
                {pageSizes.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}条/页
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}
